import React, { useEffect, useState } from "react";
import useFetchData from "../../hooks/UseFetchData";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../shared/PageHeader";
import CardHeading from "../../shared/CardHeading";
import ListDetailsCard from "../../shared/ListDetailsCard";
import { useTranslation } from "react-i18next";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { MdTrackChanges } from "react-icons/md";
import TextInput from "../../shared/TextInput";
import CustomButton from "../../shared/CustomButton";
import OrderTimeline from "./OrderTimeline";
import SkeletonContentLoading from "./SkeletonContentLoading";
import { CalculateAge } from "../../utils/CalculateAge";

const listOrderPatient = (patientData, t) => [
  {
    label: t("Name"),
    text: patientData?.name || "Unknown",
  },
  {
    label: t("Age"),
    text: CalculateAge(patientData?.dob),
  },
  {
    label: t("MRN"),
    text: patientData?.file_no || "Unknown",
  },
  {
    label: t("Document ID"),
    text: patientData?.document_id || "Unknown",
  },
  {
    label: t("Contact"),
    text: patientData?.phone || "Unknown",
  },
];

const TrackingPage = () => {
  const { t, i18n } = useTranslation();
  const { order: orderParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data, loading, executeFetch } = useFetchData();
  const [searchValue, setSearchValue] = useState(orderParam || "");
  const radiology = location?.state?.radiology || false;

  const handleSearch = (orderNum = searchValue) => {
    if (orderNum) {
      const url = radiology ? `/v3/radiology/trackOrder` : `/v3/lab/trackOrder`;
      executeFetch("POST", url, {
        order_number: orderNum,
      });
    }
  };

  useEffect(() => {
    if (orderParam) {
      handleSearch(orderParam);
    }
  }, [orderParam]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const trackingData = data?.data;
  const itemsHistoryList = trackingData?.items_history
    ? Object.values(trackingData.items_history)
    : [];

  return (
    <div className="main-page-container !overflow-y-auto scrollbar bg-[#fdfdfd]">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <PageHeader
          title={t("Track Order")}
          iconSize={20}
          Icon={i18n.language === "en" ? FaArrowLeft : FaArrowRight}
          onIconClick={() => {
            navigate("/patient-profile/summary", {
              state: { tab: radiology ? 0 : 1 },
            });
          }}
          className="!mb-0"
        />
        <div className="flex gap-2 w-full md:w-auto">
          <TextInput
            useFormik={false}
            value={searchValue}
            handleChange={(e) => setSearchValue(e.target.value)}
            onKeyPress={handleKeyPress}
            className="w-full md:w-[240px]"
            placeholder={t("Order Number...")}
          />
          <CustomButton
            label={t("Track")}
            buttonType="primary"
            icon={<MdTrackChanges />}
            onClick={() => handleSearch()}
            loading={loading}
            className="h-[38px] whitespace-nowrap"
          />
        </div>
      </div>

      {loading ? (
        <SkeletonContentLoading type="tracking" />
      ) : trackingData ? (
        <div className="space-y-4">
          <div>
            <CardHeading title={t(`Patient information`)} />
            <ListDetailsCard
              listData={listOrderPatient(trackingData?.patient_info, t)}
            />
          </div>

          <CardHeading
            title={t(`Tracking Record`) + " : " + trackingData?.order_number}
          />

          {itemsHistoryList.length > 0 && (
            <div
              className={`grid gap-4 ${itemsHistoryList.length === 1
                  ? "grid-cols-1"
                  : "grid-cols-1 lg:grid-cols-2"
                }`}
            >
              {itemsHistoryList.map((item, index) => (
                <div
                  key={item.item_id || index}
                  className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden flex flex-col"
                >
                  <div className="bg-gray-50/50 px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded bg-blue-50 flex items-center justify-center text-blue-600">
                        <i className="pi pi-box text-sm" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 text-[15px] uppercase">
                          {item.item_name}
                        </h4>
                        <span className="text-[13px] text-gray-500 font-medium">
                          {t("Code")}: {item.non_standard_code}
                        </span>
                      </div>
                    </div>
                    <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[13px] font-bold uppercase tracking-wider">
                      {item.history?.length || 0} {t("Steps")}
                    </span>
                  </div>

                  <div className="p-4 flex-1">
                    <OrderTimeline timeline={item.history} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
          <div className="bg-blue-50 p-6 rounded-full mb-4">
            <MdTrackChanges className="text-5xl text-blue-500 opacity-50" />
          </div>
          <h3 className="text-gray-600 text-base font-semibold">
            {t("Search to track order")}
          </h3>
          <p className="text-gray-400 text-sm mt-1">
            {t("Enter a valid order number above to view timeline history")}
          </p>
        </div>
      )}
    </div>
  );
};

export default TrackingPage;
